import {
  type ColorValue,
  CssColor,
  CssFont,
  type NumericValue,
  type PropertyTypeValueMap,
  StyleType,
  wixColor,
  wixFont,
} from '@wix/editor-platform-ooi-metadata-resolver';
import {
  createStylesParams,
  type ISettingsColor,
  type ISettingsFont,
  type ISiteTextPreset,
  type IStyleParam,
  type IWixStyleFont,
  StyleParamType,
  wixColorParam,
  wixFontParam,
} from '@wix/tpa-settings';
import type { CustomCssVarsFn } from '@wix/yoshi-flow-editor';

type CssColorValue = Parameters<typeof wixColor>[0];
type CssFontValue = Parameters<typeof wixFont>[0];

export const fontValueFix = (
  fontFn: Function,
  textPresets: ISiteTextPreset,
): IWixStyleFont => {
  const { value: _, ...font } = fontFn({ textPresets });

  return {
    ...font,
  } as IWixStyleFont;
};

type BooleanStyleParam = Omit<
  IStyleParam<StyleParamType.Boolean, boolean>,
  'name'
>;

const booleanParam = (defaultValue: boolean): BooleanStyleParam => ({
  type: StyleParamType.Boolean,
  getDefaultValue: () => defaultValue,
  metadata: {
    type: StyleType.booleanValue,
    defaultValue: {
      type: 'static',
      value: defaultValue,
    },
  },
});

type NumberStyleParam = Omit<
  IStyleParam<StyleParamType.Number, number>,
  'name'
>;

type StyleTypeFor<ValueType> = keyof {
  [K in keyof PropertyTypeValueMap as Required<
    PropertyTypeValueMap[K]
  > extends Required<ValueType>
    ? K
    : never]: K;
};

const numberParam = (
  type: StyleTypeFor<NumericValue>,
  defaultValue: number | undefined,
): NumberStyleParam => ({
  type: StyleParamType.Number,
  getDefaultValue: () => defaultValue as number,
  metadata: {
    type: type as StyleType.UNKNOWN_CssPropertyType, // type assertion is needed to avoid strange TypeScript error
    defaultValue: {
      type: 'static',
      value: defaultValue as number,
    },
  },
});

const numberParamWithoutInheritDesktop = (
  type: StyleTypeFor<NumericValue>,
  defaultValue: number,
): NumberStyleParam => ({
  inheritDesktop: false,
  ...numberParam(type, defaultValue),
});

const numberParamWithMobileDefault = (
  type: StyleTypeFor<NumericValue>,
  mobileDefaultValue: number,
  defaultValue: number,
): NumberStyleParam => ({
  type: StyleParamType.Number,
  getDefaultValue: ({ isMobile }) =>
    isMobile ? mobileDefaultValue : defaultValue,
  inheritDesktop: false,
  metadata: {
    type: type as StyleType.UNKNOWN_CssPropertyType, // type assertion is needed to avoid strange TypeScript error, because the enum union is too long
    defaultValue: {
      type: 'static',
      value: defaultValue,
    },
    mobileDefaultValue: {
      type: 'static',
      value: mobileDefaultValue,
    },
  },
});

type ColorStyleParam = Omit<
  IStyleParam<StyleParamType.Color, ISettingsColor>,
  'name'
>;

const colorParam = (
  type: StyleTypeFor<ColorValue>,
  preset: string,
  themeColor: CssColorValue,
): ColorStyleParam => ({
  type: StyleParamType.Color,
  getDefaultValue: wixColorParam(preset),
  metadata: {
    type,
    defaultValue: wixColor(themeColor),
  },
});

const colorParamWithOpacity = (
  type: StyleTypeFor<ColorValue>,
  preset: string,
  opacity: number,
  themeColor: CssColorValue,
): ColorStyleParam => ({
  type: StyleParamType.Color,
  getDefaultValue: wixColorParam(preset, opacity),
  metadata: {
    type,
    defaultValue: wixColor(themeColor),
  },
});

type FontStyleParam = Omit<
  IStyleParam<StyleParamType.Font, ISettingsFont>,
  'name'
>;

const fontParam = (
  preset: keyof ISiteTextPreset,
  props: { size?: number; htmlTag?: string },
  themeFont: CssFontValue,
): FontStyleParam => ({
  type: StyleParamType.Font,
  getDefaultValue: wixFontParam(preset, props),
  metadata: {
    type: StyleType.font,
    defaultValue: wixFont(themeFont),
  },
});

const fontParamWithMobileDefault = (
  preset: keyof ISiteTextPreset,
  mobileSize: number,
  size: number,
  themeFont: CssFontValue,
): FontStyleParam => ({
  type: StyleParamType.Font,
  getDefaultValue: ({ textPresets, isMobile }) =>
    fontValueFix(
      wixFontParam(preset, {
        size: isMobile ? mobileSize : size,
      }),
      textPresets,
    ),
  metadata: {
    type: StyleType.font,
    defaultValue: wixFont(themeFont),
    mobileDefaultValue: wixFont(themeFont),
  },
});

export type IStylesParams = {
  sectionTitleBookButtonVisibility: StyleParamType.Boolean;
  sectionSchedulingBookButtonVisibility: StyleParamType.Boolean;
  sectionContactBookButtonVisibility: StyleParamType.Boolean;
  sectionDescriptionBookButtonVisibility: StyleParamType.Boolean;
  sectionDetailsBookButtonVisibility: StyleParamType.Boolean;
  sectionGalleryBookButtonVisibility: StyleParamType.Boolean;
  sectionPluginBookButtonVisibility: StyleParamType.Boolean;
  sectionPolicyBookButtonVisibility: StyleParamType.Boolean;
  sectionTitleVisibility: StyleParamType.Boolean;
  sectionSchedulingVisibility: StyleParamType.Boolean;
  sectionContactVisibility: StyleParamType.Boolean;
  sectionDescriptionVisibility: StyleParamType.Boolean;
  sectionDetailsVisibility: StyleParamType.Boolean;
  sectionGalleryVisibility: StyleParamType.Boolean;
  sectionPluginVisibility: StyleParamType.Boolean;
  sectionPolicyVisibility: StyleParamType.Boolean;
  sectionTitleDivider: StyleParamType.Boolean;
  sectionSchedulingDivider: StyleParamType.Boolean;
  sectionContactDivider: StyleParamType.Boolean;
  sectionDescriptionDivider: StyleParamType.Boolean;
  sectionDetailsDivider: StyleParamType.Boolean;
  sectionGalleryDivider: StyleParamType.Boolean;
  sectionPluginDivider: StyleParamType.Boolean;
  sectionPolicyDivider: StyleParamType.Boolean;
  headerVisibility: StyleParamType.Boolean;
  headerTitleVisibility: StyleParamType.Boolean;
  headerImageVisibility: StyleParamType.Boolean;
  headerBookButtonVisibility: StyleParamType.Boolean;
  sidebarVisibility: StyleParamType.Boolean;
  onlineBadgeVisibility: StyleParamType.Boolean;
  serviceTitleVisibility: StyleParamType.Boolean;
  serviceTaglineVisibility: StyleParamType.Boolean;
  detailsDurationVisibility: StyleParamType.Boolean;
  detailsPriceVisibility: StyleParamType.Boolean;
  detailsLocationVisibility: StyleParamType.Boolean;
  businessEmailVisibility: StyleParamType.Boolean;
  businessPhoneNumberVisibility: StyleParamType.Boolean;
  businessAddressVisibility: StyleParamType.Boolean;
  locationNameVisibility: StyleParamType.Boolean;
  scheduleDurationVisibility: StyleParamType.Boolean;
  scheduleStaffVisibility: StyleParamType.Boolean;
  courseAvailability: StyleParamType.Boolean;
  showBreadcrumbs: StyleParamType.Boolean;
  headerHeight: StyleParamType.Number;
  headerSidePadding: StyleParamType.Number;
  headerImageOpacity: StyleParamType.Number;
  headerImageBackgroundColor: StyleParamType.Color;
  headerBackgroundColor: StyleParamType.Color;
  headerTitleFont: StyleParamType.Font;
  headerTitleColor: StyleParamType.Color;
  bodyBackgroundColor: StyleParamType.Color;
  bodyServiceTitleFont: StyleParamType.Font;
  bodyServiceTitleColor: StyleParamType.Color;
  bodyServiceTaglineFont: StyleParamType.Font;
  bodyServiceTaglineColor: StyleParamType.Color;
  bodySectionTitleFont: StyleParamType.Font;
  bodySectionTitleColor: StyleParamType.Color;
  bodyTextFont: StyleParamType.Font;
  bodyTextColor: StyleParamType.Color;
  courseAvailabilityTextFont: StyleParamType.Font;
  courseAvailabilityTextColor: StyleParamType.Color;
  subscriptionInfoFont: StyleParamType.Font;
  subscriptionInfoColor: StyleParamType.Color;
  bodyDividerWidth: StyleParamType.Number;
  bodyDividerColor: StyleParamType.Color;
  sidebarBackgroundColor: StyleParamType.Color;
  sidebarTitleColor: StyleParamType.Color;
  sidebarTitleFont: StyleParamType.Font;
  sidebarTextColor: StyleParamType.Color;
  sidebarTextFont: StyleParamType.Font;
  sidebarServiceTitleColor: StyleParamType.Color;
  sidebarServiceTitleFont: StyleParamType.Font;
  sidebarServiceTaglineColor: StyleParamType.Color;
  sidebarServiceTaglineFont: StyleParamType.Font;
  sidebarButtonTextColor: StyleParamType.Color;
  sidebarButtonTextFont: StyleParamType.Font;
  sidebarButtonBackgroundColor: StyleParamType.Color;
  sidebarButtonBorderColor: StyleParamType.Color;
  sidebarButtonBorderWidth: StyleParamType.Number;
  sidebarButtonCornerRadius: StyleParamType.Number;
  sidebarButtonHoverTextColor: StyleParamType.Color;
  sidebarButtonHoverBackgroundColor: StyleParamType.Color;
  sidebarButtonHoverBorderColor: StyleParamType.Color;
  sidebarWidth: StyleParamType.Number;
  sidebarSidePadding: StyleParamType.Number;
  sidebarSideSpacing: StyleParamType.Number;
  sidebarSpaceFromTheTop: StyleParamType.Number;
  headerButtonTextColor: StyleParamType.Color;
  headerButtonTextFont: StyleParamType.Font;
  headerButtonBackgroundColor: StyleParamType.Color;
  headerButtonBorderColor: StyleParamType.Color;
  headerButtonBorderWidth: StyleParamType.Number;
  headerButtonCornerRadius: StyleParamType.Number;
  headerButtonHoverTextColor: StyleParamType.Color;
  headerButtonHoverBackgroundColor: StyleParamType.Color;
  headerButtonHoverBorderColor: StyleParamType.Color;
  bodyButtonTextColor: StyleParamType.Color;
  bodyButtonTextFont: StyleParamType.Font;
  bodyButtonBackgroundColor: StyleParamType.Color;
  bodyButtonBorderColor: StyleParamType.Color;
  bodyButtonBorderWidth: StyleParamType.Number;
  bodyButtonCornerRadius: StyleParamType.Number;
  bodyButtonHoverTextColor: StyleParamType.Color;
  bodyButtonHoverBackgroundColor: StyleParamType.Color;
  bodyButtonHoverBorderColor: StyleParamType.Color;
  bodySidePadding: StyleParamType.Number;
  bodySpaceBetweenSections: StyleParamType.Number;
  firstSectionSpaceFromTheTop: StyleParamType.Number;
  backgroundColor: StyleParamType.Color;
  detailsInnerPadding: StyleParamType.Number;
  detailsBoxFont: StyleParamType.Font;
  detailsBoxTextColor: StyleParamType.Color;
  detailsBoxColor: StyleParamType.Color;
  detailsBoxBorderColor: StyleParamType.Color;
  detailsBoxBorderWidth: StyleParamType.Number;
  detailsBoxBorderRadius: StyleParamType.Number;
  priceBeforeDiscountVisibility: StyleParamType.Boolean;
  discountNameVisibility: StyleParamType.Boolean;
  priceBeforeDiscountFont: StyleParamType.Font;
  mobilePriceBeforeDiscountFontSize: StyleParamType.Number;
  priceBeforeDiscountColor: StyleParamType.Color;
  discountNameFont: StyleParamType.Font;
  mobileDiscountNameFontSize: StyleParamType.Number;
  discountNameColor: StyleParamType.Color;
  mobileSubscriptionInfoFontSize: StyleParamType.Number;
  scheduleBoxPadding: StyleParamType.Number;
  scheduleBackgroundColor: StyleParamType.Color;
  scheduleBorderColor: StyleParamType.Color;
  scheduleBorderWidth: StyleParamType.Number;
  scheduleBorderRadius: StyleParamType.Number;
  scheduleDividerColor: StyleParamType.Color;
  scheduleDividerWidth: StyleParamType.Number;
  scheduleDatesFont: StyleParamType.Font;
  scheduleDatesColor: StyleParamType.Color;
  scheduleHoursFont: StyleParamType.Font;
  scheduleHoursColor: StyleParamType.Color;
  scheduleDetailsFont: StyleParamType.Font;
  scheduleDetailsColor: StyleParamType.Color;
  scheduleLinkFont: StyleParamType.Font;
  scheduleLinkColor: StyleParamType.Color;
  galleryMasonryItemSpacing: StyleParamType.Number;
  galleryMasonryItemBorderWidth: StyleParamType.Number;
  galleryMasonryItemRadius: StyleParamType.Number;
  galleryMasonryItemBorderColor: StyleParamType.Color;
  galleryGridItemSpacing: StyleParamType.Number;
  galleryGridItemBorderWidth: StyleParamType.Number;
  galleryGridItemRadius: StyleParamType.Number;
  galleryGridItemBorderColor: StyleParamType.Color;
  gallerySliderItemSpacing: StyleParamType.Number;
  gallerySliderItemBorderWidth: StyleParamType.Number;
  gallerySliderItemRadius: StyleParamType.Number;
  gallerySliderItemBorderColor: StyleParamType.Color;
  gallerySliderArrowColor: StyleParamType.Color;
  mobileServiceTitleFontSize: StyleParamType.Number;
  mobileServiceTaglineFontSize: StyleParamType.Number;
  mobileSectionTitleFontSize: StyleParamType.Number;
  mobileTextFontSize: StyleParamType.Number;
  mobileButtonTextSize: StyleParamType.Number;
  mobileHeaderTitleFontSize: StyleParamType.Number;
  mobileHeaderButtonTextSize: StyleParamType.Number;
  mobileDetailsBoxFontSize: StyleParamType.Number;
  mobileScheduleDatesFontSize: StyleParamType.Number;
  mobileScheduleHoursFontSize: StyleParamType.Number;
  mobileScheduleDetailsFontSize: StyleParamType.Number;
  mobileScheduleLinkFontSize: StyleParamType.Number;
};

const stylesParams = createStylesParams<IStylesParams>({
  sectionTitleBookButtonVisibility: booleanParam(false),
  sectionSchedulingBookButtonVisibility: booleanParam(true),
  sectionContactBookButtonVisibility: booleanParam(false),
  sectionDescriptionBookButtonVisibility: booleanParam(false),
  sectionDetailsBookButtonVisibility: booleanParam(true),
  sectionGalleryBookButtonVisibility: booleanParam(false),
  sectionPluginBookButtonVisibility: booleanParam(false),
  sectionPolicyBookButtonVisibility: booleanParam(false),
  sectionTitleVisibility: booleanParam(true),
  sectionSchedulingVisibility: booleanParam(true),
  sectionContactVisibility: booleanParam(true),
  sectionDescriptionVisibility: booleanParam(true),
  sectionDetailsVisibility: booleanParam(true),
  sectionGalleryVisibility: booleanParam(true),
  sectionPluginVisibility: booleanParam(true),
  sectionPolicyVisibility: booleanParam(true),
  sectionTitleDivider: booleanParam(true),
  sectionSchedulingDivider: booleanParam(true),
  sectionContactDivider: booleanParam(true),
  sectionDescriptionDivider: booleanParam(true),
  sectionDetailsDivider: booleanParam(true),
  sectionGalleryDivider: booleanParam(true),
  sectionPluginDivider: booleanParam(true),
  sectionPolicyDivider: booleanParam(true),
  headerVisibility: booleanParam(true),
  headerTitleVisibility: booleanParam(false),
  headerImageVisibility: booleanParam(true),
  headerBookButtonVisibility: booleanParam(false),
  sidebarVisibility: booleanParam(false),
  onlineBadgeVisibility: booleanParam(true),
  serviceTitleVisibility: booleanParam(true),
  serviceTaglineVisibility: booleanParam(true),
  detailsDurationVisibility: booleanParam(true),
  detailsPriceVisibility: booleanParam(true),
  detailsLocationVisibility: booleanParam(true),
  priceBeforeDiscountVisibility: booleanParam(true),
  discountNameVisibility: booleanParam(true),
  businessEmailVisibility: booleanParam(true),
  businessPhoneNumberVisibility: booleanParam(true),
  businessAddressVisibility: booleanParam(true),
  locationNameVisibility: booleanParam(false),
  scheduleDurationVisibility: booleanParam(true),
  scheduleStaffVisibility: booleanParam(true),
  courseAvailability: booleanParam(true),
  showBreadcrumbs: booleanParam(false),
  headerHeight: numberParamWithMobileDefault(StyleType.height, 220, 360),
  headerSidePadding: numberParamWithoutInheritDesktop(StyleType.padding, 20),
  headerImageOpacity: numberParam(StyleType.opacity as any, 100),
  headerImageBackgroundColor: colorParam(
    StyleType.backgroundColor,
    'color-5',
    CssColor.WstSecondaryBackgroundColor,
  ),
  bodyBackgroundColor: colorParam(
    StyleType.backgroundColor,
    'color-1',
    CssColor.WstPrimaryBackgroundColor,
  ),
  headerBackgroundColor: colorParam(
    StyleType.backgroundColor,
    'color-5',
    CssColor.WstParagraph2Color,
  ),
  headerTitleFont: fontParamWithMobileDefault(
    'Page-title',
    32,
    40,
    CssFont.WstHeading2Font,
  ),
  headerTitleColor: colorParam(
    StyleType.color,
    'color-1',
    CssColor.WstPrimaryBackgroundColor,
  ),
  bodySectionTitleFont: fontParam(
    'Page-title',
    { size: 20 },
    CssFont.WstHeading2Font,
  ),
  bodySectionTitleColor: colorParam(
    StyleType.color,
    'color-5',
    CssColor.WstHeading2Color,
  ),
  bodyTextFont: fontParamWithMobileDefault(
    'Body-M',
    14,
    16,
    CssFont.WstParagraph2Font,
  ),
  bodyTextColor: colorParam(
    StyleType.color,
    'color-5',
    CssColor.WstParagraph2Color,
  ),
  courseAvailabilityTextFont: fontParamWithMobileDefault(
    'Body-M',
    14,
    16,
    CssFont.WstParagraph2Font,
  ),
  courseAvailabilityTextColor: colorParam(
    StyleType.color,
    'color-5',
    CssColor.WstParagraph2Color,
  ),
  subscriptionInfoFont: fontParam(
    'Body-M',
    { size: 14 },
    CssFont.WstParagraph2Font,
  ),
  subscriptionInfoColor: colorParam(
    StyleType.color,
    'color-5',
    CssColor.WstParagraph2Color,
  ),
  bodySidePadding: numberParamWithoutInheritDesktop(StyleType.padding, 20),
  firstSectionSpaceFromTheTop: numberParamWithoutInheritDesktop(
    StyleType.marginTop,
    48,
  ),
  bodySpaceBetweenSections: numberParamWithoutInheritDesktop(StyleType.gap, 32),
  sidebarBackgroundColor: colorParam(
    StyleType.backgroundColor,
    'color-1',
    CssColor.WstPrimaryBackgroundColor,
  ),
  sidebarTitleColor: colorParam(
    StyleType.color,
    'color-5',
    CssColor.WstParagraph2Color,
  ),
  sidebarTitleFont: fontParam(
    'Page-title',
    { size: 20 },
    CssFont.WstHeading2Font,
  ),
  sidebarTextColor: colorParam(
    StyleType.color,
    'color-5',
    CssColor.WstParagraph2Color,
  ),
  sidebarTextFont: fontParamWithMobileDefault(
    'Body-M',
    14,
    16,
    CssFont.WstParagraph2Font,
  ),
  sidebarServiceTitleColor: colorParam(
    StyleType.color,
    'color-5',
    CssColor.WstParagraph2Color,
  ),
  sidebarServiceTitleFont: fontParamWithMobileDefault(
    'Page-title',
    32,
    40,
    CssFont.WstHeading1Font,
  ),
  sidebarServiceTaglineColor: colorParam(
    StyleType.color,
    'color-5',
    CssColor.WstParagraph2Color,
  ),
  sidebarServiceTaglineFont: fontParamWithMobileDefault(
    'Body-M',
    14,
    16,
    CssFont.WstParagraph2Font,
  ),
  sidebarWidth: numberParam(StyleType.width, 30),
  sidebarSidePadding: numberParamWithoutInheritDesktop(StyleType.padding, 20),
  sidebarSideSpacing: numberParamWithoutInheritDesktop(StyleType.gap, 0),
  sidebarSpaceFromTheTop: numberParamWithoutInheritDesktop(
    StyleType.marginTop,
    48,
  ),
  backgroundColor: colorParam(
    StyleType.backgroundColor,
    'color-5',
    CssColor.WstSecondaryBackgroundColor,
  ),
  detailsInnerPadding: numberParamWithoutInheritDesktop(StyleType.padding, 16),
  detailsBoxFont: fontParamWithMobileDefault(
    'Body-M',
    14,
    16,
    CssFont.WstParagraph2Font,
  ),
  detailsBoxTextColor: colorParam(
    StyleType.color,
    'color-5',
    CssColor.WstParagraph2Color,
  ),
  detailsBoxColor: colorParamWithOpacity(
    StyleType.backgroundColor,
    'color-1',
    0,
    CssColor.WstPrimaryBackgroundColor,
  ),
  detailsBoxBorderColor: colorParamWithOpacity(
    StyleType.borderColor,
    'color-5',
    0.2,
    CssColor.WstSystemLine1Color,
  ),
  detailsBoxBorderWidth: numberParam(StyleType.borderWidth, 1),
  priceBeforeDiscountFont: fontParam(
    'Body-M',
    { size: 16 },
    CssFont.WstParagraph2Font,
  ),
  mobilePriceBeforeDiscountFontSize: numberParam(StyleType.fontSize, 14),
  priceBeforeDiscountColor: colorParam(
    StyleType.color,
    'color-3',
    CssColor.WstButtonSecondaryColor,
  ),
  discountNameFont: fontParam(
    'Body-M',
    { size: 14 },
    CssFont.WstParagraph2Font,
  ),
  mobileDiscountNameFontSize: numberParam(StyleType.fontSize, 14),
  discountNameColor: colorParam(
    StyleType.color,
    'color-8',
    CssColor.WstButtonPrimaryColor,
  ),
  mobileSubscriptionInfoFontSize: numberParam(StyleType.fontSize, 14),
  sidebarButtonTextColor: colorParam(
    StyleType.color,
    'color-1',
    CssColor.WstPrimaryBackgroundColor,
  ),
  sidebarButtonTextFont: fontParamWithMobileDefault(
    'Body-M',
    14,
    16,
    CssFont.WstParagraph2Font,
  ),
  sidebarButtonBackgroundColor: colorParam(
    StyleType.backgroundColor,
    'color-8',
    CssColor.WstLinksAndActionsColor,
  ),
  sidebarButtonBorderColor: colorParam(
    StyleType.borderColor,
    'color-8',
    CssColor.WstLinksAndActionsColor,
  ),
  sidebarButtonBorderWidth: numberParam(StyleType.borderWidth, 0),
  sidebarButtonCornerRadius: numberParam(StyleType.borderRadius, 1),
  sidebarButtonHoverTextColor: colorParam(
    StyleType.color,
    'color-1',
    CssColor.WstPrimaryBackgroundColor,
  ),
  sidebarButtonHoverBackgroundColor: colorParam(
    StyleType.backgroundColor,
    'color-8',
    CssColor.WstLinksAndActionsColor,
  ),
  sidebarButtonHoverBorderColor: colorParam(
    StyleType.borderColor,
    'color-8',
    CssColor.WstLinksAndActionsColor,
  ),
  bodyButtonTextColor: colorParam(
    StyleType.color,
    'color-1',
    CssColor.WstButtonPrimaryColor,
  ),
  bodyButtonTextFont: fontParamWithMobileDefault(
    'Body-M',
    14,
    16,
    CssFont.WstParagraph2Font,
  ),
  bodyButtonBackgroundColor: colorParamWithOpacity(
    StyleType.backgroundColor,
    'color-8',
    1,
    CssColor.WstButtonSecondaryBackgroundColor,
  ),
  bodyButtonBorderColor: colorParamWithOpacity(
    StyleType.borderColor,
    'color-8',
    1,
    CssColor.WstButtonPrimaryBorderBottomColor,
  ),
  bodyButtonBorderWidth: numberParam(StyleType.borderWidth, 0),
  bodyButtonCornerRadius: numberParam(StyleType.borderRadius, 0),
  bodyButtonHoverTextColor: colorParam(
    StyleType.color,
    'color-1',
    CssColor.WstButtonPrimaryColor,
  ),
  bodyButtonHoverBackgroundColor: colorParam(
    StyleType.backgroundColor,
    'color-8',
    CssColor.WstButtonSecondaryBackgroundColor,
  ),
  bodyButtonHoverBorderColor: colorParam(
    StyleType.borderColor,
    'color-8',
    CssColor.WstButtonPrimaryBorderBottomColor,
  ),
  headerButtonTextColor: colorParam(
    StyleType.color,
    'color-5',
    CssColor.WstParagraph2Color,
  ),
  headerButtonTextFont: fontParamWithMobileDefault(
    'Body-M',
    14,
    16,
    CssFont.WstParagraph2Font,
  ),
  headerButtonBackgroundColor: colorParam(
    StyleType.backgroundColor,
    'color-1',
    CssColor.WstPrimaryBackgroundColor,
  ),
  headerButtonBorderColor: colorParam(
    StyleType.borderColor,
    'color-1',
    CssColor.WstPrimaryBackgroundColor,
  ),
  headerButtonBorderWidth: numberParam(StyleType.borderWidth, 0),
  headerButtonCornerRadius: numberParam(StyleType.borderRadius, 0),
  headerButtonHoverTextColor: colorParam(
    StyleType.color,
    'color-5',
    CssColor.WstParagraph2Color,
  ),
  headerButtonHoverBackgroundColor: colorParam(
    StyleType.backgroundColor,
    'color-1',
    CssColor.WstPrimaryBackgroundColor,
  ),
  headerButtonHoverBorderColor: colorParam(
    StyleType.borderColor,
    'color-1',
    CssColor.WstPrimaryBackgroundColor,
  ),
  bodyDividerWidth: numberParam(StyleType.borderWidth, 1),
  detailsBoxBorderRadius: numberParam(StyleType.borderRadius, 0),
  bodyDividerColor: colorParamWithOpacity(
    StyleType.borderColor,
    'color-5',
    0.2,
    CssColor.WstSystemLine1Color,
  ),
  bodyServiceTitleFont: fontParamWithMobileDefault(
    'Page-title',
    32,
    40,
    CssFont.WstHeading2Font,
  ),
  bodyServiceTitleColor: colorParam(
    StyleType.color,
    'color-5',
    CssColor.WstHeading2Color,
  ),
  bodyServiceTaglineFont: fontParamWithMobileDefault(
    'Body-M',
    14,
    16,
    CssFont.WstParagraph2Font,
  ),
  bodyServiceTaglineColor: colorParam(
    StyleType.color,
    'color-5',
    CssColor.WstParagraph2Color,
  ),
  scheduleBoxPadding: numberParamWithMobileDefault(StyleType.padding, 20, 28),
  scheduleBackgroundColor: colorParamWithOpacity(
    StyleType.backgroundColor,
    'color-1',
    0,
    CssColor.WstPrimaryBackgroundColor,
  ),
  scheduleBorderColor: colorParamWithOpacity(
    StyleType.borderColor,
    'color-5',
    0.2,
    CssColor.WstSystemLine1Color,
  ),
  scheduleBorderWidth: numberParam(StyleType.borderWidth, 1),
  scheduleBorderRadius: numberParam(StyleType.borderRadius, 0),
  scheduleDividerColor: colorParamWithOpacity(
    StyleType.borderColor,
    'color-5',
    0.2,
    CssColor.WstSystemLine1Color,
  ),
  scheduleDividerWidth: numberParam(StyleType.borderWidth, 1),
  scheduleDatesFont: fontParamWithMobileDefault(
    'Body-M',
    14,
    16,
    CssFont.WstParagraph2Font,
  ),
  scheduleDatesColor: colorParam(
    StyleType.color,
    'color-5',
    CssColor.WstParagraph2Color,
  ),
  scheduleHoursFont: fontParamWithMobileDefault(
    'Body-M',
    14,
    16,
    CssFont.WstParagraph2Font,
  ),
  scheduleHoursColor: colorParam(
    StyleType.color,
    'color-5',
    CssColor.WstParagraph2Color,
  ),
  scheduleDetailsFont: fontParam(
    'Body-M',
    { size: 14 },
    CssFont.WstParagraph2Font,
  ),
  scheduleDetailsColor: colorParam(
    StyleType.color,
    'color-4',
    CssColor.WstShade3Color,
  ),
  scheduleLinkFont: fontParamWithMobileDefault(
    'Body-M',
    14,
    16,
    CssFont.WstParagraph2Font,
  ),
  scheduleLinkColor: colorParam(
    StyleType.color,
    'color-8',
    CssColor.WstLinksAndActionsColor,
  ),
  galleryMasonryItemSpacing: numberParamWithoutInheritDesktop(
    StyleType.gap,
    12,
  ),
  galleryMasonryItemBorderWidth: numberParam(StyleType.borderWidth, 0),
  galleryMasonryItemRadius: numberParam(StyleType.borderRadius, 0),
  galleryMasonryItemBorderColor: colorParamWithOpacity(
    StyleType.borderColor,
    'color-5',
    0.2,
    CssColor.WstSystemLine1Color,
  ),
  galleryGridItemSpacing: numberParamWithoutInheritDesktop(StyleType.gap, 12),
  galleryGridItemBorderWidth: numberParam(StyleType.borderWidth, 0),
  galleryGridItemRadius: numberParam(StyleType.borderRadius, 0),
  galleryGridItemBorderColor: colorParamWithOpacity(
    StyleType.borderColor,
    'color-5',
    0.2,
    CssColor.WstSystemLine1Color,
  ),
  gallerySliderItemSpacing: numberParamWithoutInheritDesktop(StyleType.gap, 12),
  gallerySliderItemBorderWidth: numberParam(StyleType.borderWidth, 0),
  gallerySliderItemRadius: numberParam(StyleType.borderRadius, 0),
  gallerySliderItemBorderColor: colorParamWithOpacity(
    StyleType.borderColor,
    'color-5',
    0.2,
    CssColor.WstSystemLine1Color,
  ),
  gallerySliderArrowColor: colorParam(
    StyleType.color,
    'color-5',
    CssColor.WstParagraph2Color,
  ),
  mobileServiceTitleFontSize: numberParam(StyleType.fontSize, 32),
  mobileServiceTaglineFontSize: numberParam(StyleType.fontSize, 14),
  mobileSectionTitleFontSize: numberParam(StyleType.fontSize, 20),
  mobileTextFontSize: numberParam(StyleType.fontSize, 14),
  mobileButtonTextSize: numberParam(StyleType.fontSize, 14),
  mobileHeaderTitleFontSize: numberParam(StyleType.fontSize, 32),
  mobileHeaderButtonTextSize: numberParam(StyleType.fontSize, 14),
  mobileDetailsBoxFontSize: numberParam(StyleType.fontSize, 14),
  mobileScheduleDatesFontSize: numberParam(StyleType.fontSize, 14),
  mobileScheduleHoursFontSize: numberParam(StyleType.fontSize, 14),
  mobileScheduleDetailsFontSize: numberParam(StyleType.fontSize, 14),
  mobileScheduleLinkFontSize: numberParam(StyleType.fontSize, 14),
});

export default stylesParams;

// noinspection JSUnusedGlobalSymbols
export const customCssVars: CustomCssVarsFn = ({ styleParams }) => {
  const displayStyle = (
    booleanStyleParam: keyof typeof stylesParams,
    visibleDisplay = 'block',
  ) => (styleParams.booleans[booleanStyleParam] ? visibleDisplay : 'none');

  const calculateMargin = (booleanStyleParam: keyof typeof stylesParams) =>
    styleParams.booleans[booleanStyleParam]
      ? styleParams.numbers.bodySpaceBetweenSections
      : 0;

  return {
    headerDisplay: displayStyle('headerVisibility', 'flex'),
    headerTitleDisplay: displayStyle('headerTitleVisibility'),
    headerImageDisplay: displayStyle('headerImageVisibility'),
    headerBookButtonDisplay: displayStyle('headerBookButtonVisibility'),
    sidebarDisplay: displayStyle('sidebarVisibility'),
    onlineBadgeDisplay: displayStyle('onlineBadgeVisibility'),
    serviceTitleDisplay: displayStyle('serviceTitleVisibility'),
    serviceTaglineDisplay: displayStyle('serviceTaglineVisibility'),
    detailsDurationDisplay: displayStyle('detailsDurationVisibility'),
    detailsPriceDisplay: displayStyle('detailsPriceVisibility'),
    detailsLocationDisplay: displayStyle('detailsLocationVisibility'),
    priceBeforeDiscountDisplay: displayStyle('priceBeforeDiscountVisibility'),
    detailsDiscountNameDisplay: displayStyle('discountNameVisibility', 'flex'),
    businessEmailDisplay: displayStyle('businessEmailVisibility'),
    businessPhoneNumberDisplay: displayStyle('businessPhoneNumberVisibility'),
    businessAddressDisplay: displayStyle('businessAddressVisibility'),
    locationNameDisplay: displayStyle('locationNameVisibility'),
    scheduleDurationDisplay: displayStyle('scheduleDurationVisibility'),
    scheduleStaffDisplay: displayStyle('scheduleStaffVisibility'),
    courseAvailabilityDisplay: displayStyle('courseAvailability'),
    sectionTitleBookButtonDisplay: displayStyle(
      'sectionTitleBookButtonVisibility',
    ),
    sectionSchedulingBookButtonDisplay: displayStyle(
      'sectionSchedulingBookButtonVisibility',
    ),
    sectionContactBookButtonDisplay: displayStyle(
      'sectionContactBookButtonVisibility',
    ),
    sectionDescriptionBookButtonDisplay: displayStyle(
      'sectionDescriptionBookButtonVisibility',
    ),
    sectionDetailsBookButtonDisplay: displayStyle(
      'sectionDetailsBookButtonVisibility',
    ),
    sectionGalleryBookButtonDisplay: displayStyle(
      'sectionGalleryBookButtonVisibility',
    ),
    sectionPluginBookButtonDisplay: displayStyle(
      'sectionPluginBookButtonVisibility',
    ),
    sectionPolicyBookButtonDisplay: displayStyle(
      'sectionPolicyBookButtonVisibility',
    ),
    sectionTitleDisplay: displayStyle('sectionTitleVisibility'),
    sectionSchedulingDisplay: displayStyle('sectionSchedulingVisibility'),
    sectionContactDisplay: displayStyle('sectionContactVisibility'),
    sectionDescriptionDisplay: displayStyle('sectionDescriptionVisibility'),
    sectionDetailsDisplay: displayStyle('sectionDetailsVisibility'),
    sectionGalleryDisplay: displayStyle('sectionGalleryVisibility'),
    sectionPluginDisplay: displayStyle('sectionPluginVisibility'),
    sectionPolicyDisplay: displayStyle('sectionPolicyVisibility'),
    SectionTitleDividerDisplay: displayStyle('sectionTitleDivider'),
    sectionSchedulingDividerDisplay: displayStyle('sectionSchedulingDivider'),
    sectionContactDividerDisplay: displayStyle('sectionContactDivider'),
    sectionDescriptionDividerDisplay: displayStyle('sectionDescriptionDivider'),
    sectionDetailsDividerDisplay: displayStyle('sectionDetailsDivider'),
    sectionGalleryDividerDisplay: displayStyle('sectionGalleryDivider'),
    sectionPluginDividerDisplay: displayStyle('sectionPluginDivider'),
    sectionPolicyDividerDisplay: displayStyle('sectionPolicyDivider'),
    sectionTitleMargin: calculateMargin('sectionTitleDivider'),
    sectionSchedulingMargin: calculateMargin('sectionSchedulingDivider'),
    sectionContactMargin: calculateMargin('sectionContactDivider'),
    sectionDescriptionMargin: calculateMargin('sectionDescriptionDivider'),
    sectionDetailsMargin: calculateMargin('sectionDetailsDivider'),
    sectionGalleryMargin: calculateMargin('sectionGalleryDivider'),
    sectionPluginMargin: calculateMargin('sectionPluginDivider'),
    sectionPolicyMargin: calculateMargin('sectionPolicyDivider'),
    breadcrumbsDisplay: displayStyle('showBreadcrumbs'),
  };
};
